import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { supabase } from '../lib/supabase.ts';
import { callApi } from '../lib/api.ts';
import { Toast, type ToastState } from './Toast.tsx';
import { packForKind } from '../lib/mediaPacks.ts';
import {
  VIDEO_PACK,
  serializeTags,
  serializeKeyMoments,
  normalizePackValue,
  type VideoPackField,
} from '../lib/videoPack.ts';
import { runBulk, reconcileMessage, deleteGroupWithMedia } from '../lib/bulkMedia.ts';
import type { Client } from '../lib/types.ts';
import { friendlyError } from '../lib/friendlyError.ts';
import { btnSolid, sectionTitle } from '../lib/ui.ts';
import { FlapText } from './FlapText.tsx';
import { scoreMedia, healthFieldsFor, hasField } from '../lib/mediaHealth.ts';
import { MediaDetailModal } from './MediaDetailModal.tsx';
import { MediaSpreadsheet } from './MediaSpreadsheet.tsx';
import type { SavePatch } from '../lib/mediaFieldPatch.ts';

// #GR9(i): persist which galleries/pool are EXPANDED across tab switches (the step
// remounts, so native <details open> would re-expand everything → endless scroll at
// 500+ images). Keyed by client; the set holds open group ids + a '__pool__' sentinel.
// Default (nothing stored) = ALL COLLAPSED, so returning to the step is compact.
const POOL_KEY = '__pool__';
const openStorageKey = (clientId: string) => `alchmy:mediaOpen:${clientId}`;
function loadOpen(clientId: string): Set<string> {
  try {
    const raw = localStorage.getItem(openStorageKey(clientId));
    return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
  } catch {
    return new Set();
  }
}
function saveOpen(clientId: string, open: Set<string>) {
  try {
    localStorage.setItem(openStorageKey(clientId), JSON.stringify([...open]));
  } catch {
    /* storage unavailable — collapse still works this session, just not persisted */
  }
}

/**
 * CMS structuring view (content-media.md M3 / media-model.md CORE+PACK). Turns the flat
 * catalogue (M2) into galleries/projects the build (M4) consumes: create/rename/reorder/
 * delete groups, drag media in/out (a loose pool + each group's contents), per-item
 * caption + alt, a group cover, and the per-type photography PACK (media_groups.metadata).
 * Reads under RLS; every write drives the M3a endpoints (scoped + is_live-blocked). On a
 * live client all writes are hidden/disabled (the endpoints 403 regardless).
 */

interface Grp {
  id: string;
  name: string;
  slug: string;
  kind: 'gallery' | 'project';
  sort_order: number;
  cover_media_id: string | null;
  metadata: Record<string, unknown>;
}
interface Med {
  id: string;
  kind: 'image' | 'video';
  url: string | null;
  poster: string | null;
  alt: string;
  caption: string | null;
  group_id: string | null;
  sort_order: number;
  width: number | null;
  height: number | null;
  original_filename: string | null;
  metadata: Record<string, unknown> | null;
}

/** Orientation cue from the stored intrinsic dimensions (#9a). */
function orientationOf(m: { width: number | null; height: number | null }): 'portrait' | 'landscape' | 'square' | null {
  if (!m.width || !m.height) return null;
  if (Math.abs(m.width - m.height) <= 1) return 'square';
  return m.width > m.height ? 'landscape' : 'portrait';
}

/** Visual-CMS card title: the editorial title, else the (basename) filename, else the kind. */
function titleOf(m: Med): string {
  const t = m.metadata && typeof m.metadata.title === 'string' ? m.metadata.title.trim() : '';
  if (t) return t;
  const fn = (m.original_filename ?? '').split('/').pop() ?? '';
  return fn || m.kind;
}
/** Card subline — the editorial category, else a dash. */
function categoryOf(m: Med): string {
  const c = m.metadata && typeof m.metadata.category === 'string' ? m.metadata.category.trim() : '';
  return c || '—';
}

export function MediaStructuring({
  client,
  version,
  onChange,
  uploader,
  canMaintainLive = false,
}: {
  client: Client;
  version?: number;
  onChange?: () => void;
  // The Step-2 uploader, embedded right after the create affordance so ONE component
  // owns the full vertical order (create → upload → galleries → catalogue/pool).
  uploader?: ReactNode;
  /** go-live-protection §3a: an admin maintaining a maintainable live client — the backend
   *  liveGuard('site-write') allow-condition. True → the EDIT controls work (edits preview). */
  canMaintainLive?: boolean;
}) {
  // `live` now means the EDIT-LOCK: a live client whose edits are NOT open to this caller
  // (non-admin or protected slug). All the write controls below gate on it, so admin
  // maintenance unfreezes them. `isLiveClient` is the raw fact — used only where the control
  // stays off for EVERYONE on a live client (Export → media-export still 403s, deferred fold-in).
  const isLiveClient = client.is_live;
  const live = isLiveClient && !canMaintainLive;
  // Visual CMS (visual-cms.md §4): the ONE remaining view control — Gallery (cards) ⇄
  // Spreadsheet (rows). Spreadsheet is a later slice (Slice 3); the pill lands now so the
  // affordance is obvious, with Spreadsheet shown as coming. Gallery is the live view.
  const [view, setView] = useState<'gallery' | 'spreadsheet'>('gallery');
  // Slice 4: search (titles · tags · filenames) + a "Needs attention" (gaps-only) filter,
  // applied to BOTH the gallery cards and the spreadsheet. Pure UI — never a write.
  const [search, setSearch] = useState('');
  const [needsOnly, setNeedsOnly] = useState(false);
  const [groups, setGroups] = useState<Grp[]>([]);
  const [media, setMedia] = useState<Med[]>([]);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [newName, setNewName] = useState('');
  const [newKind, setNewKind] = useState<'gallery' | 'project'>('project');
  const [busy, setBusy] = useState(false);
  // #9b: which group is in drag-to-Arrange mode (one at a time); the in-flight drag idx.
  const [arrangeId, setArrangeId] = useState<string | null>(null);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  // #GR9(i): persisted expand/collapse (see loadOpen) + per-card refs for jump-to-nav.
  const [openIds, setOpenIds] = useState<Set<string>>(() => loadOpen(client.id));
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Reload persisted collapse-state when the client changes (no remount guaranteed).
  useEffect(() => setOpenIds(loadOpen(client.id)), [client.id]);

  const isOpen = (id: string) => openIds.has(id);
  const toggleOpen = (id: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      saveOpen(client.id, next);
      return next;
    });
  };
  // Jump-to-gallery: expand the target + smooth-scroll its card into view.
  const jumpTo = (id: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev).add(id);
      saveOpen(client.id, next);
      return next;
    });
    requestAnimationFrame(() => cardRefs.current.get(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };

  // #GR9(a) multi-select: a flat set of selected media ids, plus the last-clicked id so
  // shift-click selects a contiguous RANGE (in the on-screen order built below). #GR9(b)
  // drag-to-assign reuses the same selection (drag a selected item → move the whole set).
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const lastClickRef = useRef<string | null>(null);
  const dragIdRef = useRef<string | null>(null);
  const [dropId, setDropId] = useState<string | null>(null);
  // Bulk-move target ('' = the unassigned pool).
  const [bulkTarget, setBulkTarget] = useState<string>('');
  // #GR9(c) grid: the per-item edit affordances (alt/caption/assign/cover/move) don't
  // tile in a dense grid, so they live in an expandable editor — one open at a time.
  const [editingId, setEditingId] = useState<string | null>(null);
  // #GR9(e): which group is showing the two-option delete confirm (keep photos vs delete
  // photos too). A plain window.confirm can't offer two clear destructive choices.
  const [confirmDelId, setConfirmDelId] = useState<string | null>(null);
  // Slice 2 (visual-cms.md §4): the detail MODAL — the id of the media opened in the centred
  // pop-up (null = closed). Prev/next walk `orderedIds` (the same on-screen order a range
  // select uses), so the modal walks the catalogue exactly as it's laid out.
  const [detailId, setDetailId] = useState<string | null>(null);

  const clearSel = () => {
    setSelected(new Set());
    lastClickRef.current = null;
  };
  // Drop the selection + close the detail modal whenever the client changes (ids belong to
  // the old client).
  useEffect(clearSel, [client.id]);
  useEffect(() => setDetailId(null), [client.id]);

  const load = useCallback(async () => {
    const [g, m] = await Promise.all([
      supabase
        .from('media_groups')
        .select('id, name, slug, kind, sort_order, cover_media_id, metadata')
        .eq('client_id', client.id)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true }),
      supabase
        .from('media')
        .select('id, kind, url, poster, alt, caption, group_id, sort_order, width, height, original_filename, metadata')
        .eq('client_id', client.id)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true }),
    ]);
    setGroups((g.data as Grp[] | null) ?? []);
    setMedia((m.data as Med[] | null) ?? []);
  }, [client.id]);

  useEffect(() => {
    void load();
  }, [load, version]);

  function err(e: unknown) {
    setToast({ message: friendlyError(e, (e as Error).message), kind: 'error' });
  }
  async function run(fn: () => Promise<unknown>, ok?: string) {
    setBusy(true);
    try {
      await fn();
      await load();
      onChange?.(); // refresh the sibling uploader view too (#12 — both views, no manual reload)
      if (ok) setToast({ message: ok, kind: 'success' });
    } catch (e) {
      err(e);
    } finally {
      setBusy(false);
    }
  }
  const api = (path: string, body: unknown) => callApi(path, { method: 'POST', body: JSON.stringify({ clientId: client.id, ...(body as object) }) });

  // Order #1: export the media catalogue to the client's branch (sites/<slug>/src/data/media.ts) so
  // a hand-build session can resolve films/images by filename. The primary trigger for the ③ lane
  // (the engine's build-kickoff never fires there). Idempotent + is_live-guarded server-side.
  const [exporting, setExporting] = useState(false);
  async function exportMedia() {
    setExporting(true);
    try {
      type ExportPart = { rendered: boolean; count: number; skipped?: boolean };
      const r = (await api('media-export', {})) as { media: ExportPart; videos: ExportPart };
      const m = r.media;
      const v = r.videos;
      const vClause = v.count ? ` · video-SEO ${v.count}` : ''; // videos.ts entries (name+description videos)
      const message =
        !m.count && !m.rendered && m.skipped
          ? 'No media to export yet — upload some first.'
          : !m.rendered && m.skipped && !v.rendered
            ? `Already up to date on the branch — media ${m.count}${vClause}.`
            : `Exported to the branch — media ${m.count}${vClause} (src/data).`;
      setToast({ message, kind: 'success' });
    } catch (e) {
      err(e);
    } finally {
      setExporting(false);
    }
  }

  // VS-1b: retroactively apply a filled CSV/JSON manifest to the EXISTING catalogue
  // (a video client sends the PACK after the videos are up). Matches by original_filename;
  // merges metadata/alt; surfaces unmatched filenames so a typo isn't silent.
  const [applyMsg, setApplyMsg] = useState<string | null>(null);
  // Slice 1e: the Apply-CSV block doubles as a drop target (dragover styling only; the drop
  // calls the SAME applyManifest handler as the button — one code path).
  const [csvDragOver, setCsvDragOver] = useState(false);
  async function applyManifest(file: File) {
    setBusy(true);
    setApplyMsg(null);
    try {
      const text = await file.text();
      const r = await callApi<{ applied: number; matched: number; unmatched: string[]; failed: { id: string }[]; parseErrors: string[] }>(
        'media-apply-manifest',
        { method: 'POST', body: JSON.stringify({ clientId: client.id, text }) },
      );
      await load();
      onChange?.();
      const parts = [`Applied metadata to ${r.applied} file${r.applied === 1 ? '' : 's'}.`];
      if (r.unmatched.length) {
        parts.push(`${r.unmatched.length} row${r.unmatched.length === 1 ? '' : 's'} matched no file: ${r.unmatched.slice(0, 8).join(', ')}${r.unmatched.length > 8 ? '…' : ''}`);
      }
      if (r.failed.length) parts.push(`${r.failed.length} failed to save.`);
      if (r.parseErrors.length) parts.push(`${r.parseErrors.length} unreadable row${r.parseErrors.length === 1 ? '' : 's'}.`);
      // The apply SUCCEEDED unless rows genuinely failed to save. Unmatched filenames + unreadable
      // rows are normal partial-match outcomes (a typo'd filename, a stray header) — surfaced in the
      // detail message below, NOT an error. Red only on a real failure (previously any unmatched row
      // flipped the toast red on a successful apply — designers thought it failed and re-ran).
      setToast({ message: parts[0], kind: r.failed.length ? 'error' : 'success' });
      setApplyMsg(parts.join(' '));
    } catch (e) {
      err(e);
    } finally {
      setBusy(false);
    }
  }

  // #11b backfill: a rendered image with null dims self-heals — read the natural size
  // off the loaded <img> and persist once (no re-upload of Fred's existing 68). Silent +
  // fire-and-forget; updates local state so the chip/aspect appear immediately.
  function healDims(m: Med, naturalWidth: number, naturalHeight: number) {
    if (m.kind !== 'image' || m.width || m.height || !naturalWidth || !naturalHeight) return;
    setMedia((cur) => cur.map((x) => (x.id === m.id ? { ...x, width: naturalWidth, height: naturalHeight } : x)));
    void callApi('media-update', {
      method: 'POST',
      body: JSON.stringify({ clientId: client.id, mediaId: m.id, width: naturalWidth, height: naturalHeight }),
    }).catch(() => {
      /* best-effort heal — it'll retry on the next view */
    });
  }

  // Slice 2: the detail modal's save-as-you-type sink. Writes ONE item's own fields via
  // media-update (which merges metadata), then optimistically mirrors into local `media` so
  // the card behind the modal reflects the edit the moment it lands — no reload (quiet, like
  // the "✓ Saved" tick). is_live is already 403'd server-side AND the modal disables fields.
  const saveItem = useCallback(
    async (id: string, patch: SavePatch) => {
      await callApi('media-update', {
        method: 'POST',
        body: JSON.stringify({ clientId: client.id, mediaId: id, ...patch.apiBody }),
      });
      setMedia((cur) => cur.map((x) => (x.id === id ? { ...x, ...patch.local } : x)));
    },
    [client.id],
  );

  // ---- group ops ----
  const createGroup = () => {
    const name = newName.trim();
    if (!name) return;
    void run(async () => {
      await api('media-group-create', { name, kind: newKind });
      setNewName('');
    }, `Created “${name}”.`);
  };
  const renameGroup = (id: string, name: string) => {
    const v = name.trim();
    const g = groups.find((x) => x.id === id);
    if (!v || !g || v === g.name) return;
    void run(() => api('media-group-update', { groupId: id, name: v }));
  };
  const moveGroup = (idx: number, dir: -1 | 1) => {
    const j = idx + dir;
    if (j < 0 || j >= groups.length) return;
    const a = groups[idx];
    const b = groups[j];
    void run(() =>
      Promise.all([
        api('media-group-update', { groupId: a.id, sortOrder: b.sort_order }),
        api('media-group-update', { groupId: b.id, sortOrder: a.sort_order }),
      ]),
    );
  };
  // #GR8: change a group's type after creation (gallery ⇄ project). The per-type PACK
  // fields re-key off `kind` automatically (packForKind). Existing metadata is left
  // intact on switch — harmless: the new type simply won't render non-matching keys, and
  // switching back restores them (no destructive pruning, no accidental field loss).
  const changeKind = (g: Grp, kind: 'gallery' | 'project') => {
    if (kind === g.kind) return;
    void run(() => api('media-group-update', { groupId: g.id, kind }), `Changed “${g.name}” to ${kind}.`);
  };

  // #GR9(e) option A (default/safe): delete the group, its media returns to the pool.
  const deleteGroupKeepPhotos = (g: Grp) => {
    setConfirmDelId(null);
    void run(() => api('media-group-delete', { groupId: g.id }), `Deleted “${g.name}” — photos kept in the pool.`);
  };
  // #GR9(e) option B (destructive): delete the group AND permanently delete its N photos
  // (CF teardown per item via media-delete), then the now-empty group. Honest
  // reconciliation on the fan-out — a shortfall is surfaced, never a silent partial.
  const deleteGroupWithPhotos = async (g: Grp) => {
    setConfirmDelId(null);
    const ids = inGroup(g.id).map((m) => m.id);
    setBusy(true);
    try {
      const r = await deleteGroupWithMedia(
        ids,
        (id) => api('media-delete', { mediaId: id }),
        () => api('media-group-delete', { groupId: g.id }),
      );
      await load();
      onChange?.(); // #12 cross-view refresh
      clearSel();
      setToast(
        r.failed.length === 0
          ? { message: `Deleted “${g.name}” and its ${r.total} photo${r.total === 1 ? '' : 's'}.`, kind: 'success' }
          : { message: `Deleted “${g.name}” · ${r.ok} of ${r.total} photos deleted · ${r.failed.length} failed.`, kind: 'error' },
      );
    } catch (e) {
      err(e);
    } finally {
      setBusy(false);
    }
  };
  const setCover = (groupId: string, mediaId: string) => {
    void run(() => api('media-group-update', { groupId, coverMediaId: mediaId }), 'Cover set.');
  };
  const savePack = (g: Grp, key: string, value: string) => {
    const cur = (g.metadata?.[key] ?? '') as string;
    if (value === cur) return;
    const metadata = { ...g.metadata, [key]: value };
    if (!value) delete metadata[key];
    void run(() => api('media-group-update', { groupId: g.id, metadata }));
  };

  // ---- media ops ----
  const assign = (mediaId: string, groupId: string | null) => {
    void run(() => api('media-assign', { mediaId, groupId }));
  };
  // #GR9(d): destructive-action context — the confirm names the item AND the group it's
  // in, so a delete can't hit the wrong folder's photo unnoticed (Neil's Lifestyle loss).
  const removeMedia = (id: string, label: string, groupName: string) => {
    if (!confirm(`Delete "${label}" from ${groupName}? This removes the file from Cloudflare too and can't be undone.`)) return;
    void run(() => api('media-delete', { mediaId: id }), 'Deleted.');
  };
  const editField = (id: string, field: 'caption' | 'alt', value: string) => {
    const m = media.find((x) => x.id === id);
    const cur = (field === 'caption' ? m?.caption ?? '' : m?.alt ?? '');
    if (!m || value === cur) return;
    void run(() => api('media-update', { mediaId: id, [field]: value }));
  };
  // VS-1: save ONE per-video PACK field into media.metadata (the JSONB bag). The value is
  // normalized to its stored shape (arrays for tags/keyMoments; trimmed string else; empty
  // → null so the server drops the key). Optimistically mirror into local state so the
  // editor re-seeds from the latest value; a no-op change is skipped.
  const editMeta = (id: string, f: VideoPackField, raw: string) => {
    const m = media.find((x) => x.id === id);
    if (!m) return;
    const next = normalizePackValue(f.type, raw);
    const cur = m.metadata?.[f.key] ?? null;
    if (JSON.stringify(next ?? null) === JSON.stringify(cur)) return;
    const merged: Record<string, unknown> = { ...m.metadata };
    if (next == null) delete merged[f.key];
    else merged[f.key] = next;
    setMedia((list) => list.map((x) => (x.id === id ? { ...x, metadata: merged } : x)));
    void run(() => api('media-update', { mediaId: id, metadata: { [f.key]: next } }));
  };
  // Re-order within a group. A pairwise swap of sort_order is a no-op when the values
  // are equal (legacy rows are all 0) — so instead reorder the array and REINDEX the
  // whole group to contiguous 0..n-1, persisting only the rows whose order changed.
  const moveInGroup = (list: Med[], idx: number, dir: -1 | 1) => {
    const j = idx + dir;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[idx], next[j]] = [next[j], next[idx]];
    const writes = next
      .map((m, k) => (m.sort_order === k ? null : api('media-update', { mediaId: m.id, sortOrder: k })))
      .filter(Boolean) as Promise<unknown>[];
    void run(() => Promise.all(writes));
  };

  // Drag-to-arrange (#9b): move an item from one position to another, then reindex the
  // group to contiguous 0..n-1 (persist only the rows whose order changed).
  const reorderInGroup = (list: Med[], from: number, to: number) => {
    if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return;
    const next = [...list];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    const writes = next
      .map((m, k) => (m.sort_order === k ? null : api('media-update', { mediaId: m.id, sortOrder: k })))
      .filter(Boolean) as Promise<unknown>[];
    if (writes.length) void run(() => Promise.all(writes));
  };

  const pool = media.filter((m) => !m.group_id);
  const inGroup = (gid: string) => media.filter((m) => m.group_id === gid);
  // #GR7: nudge only when there's loose media but nowhere to file it yet.
  const showNudge = !live && pool.length > 0 && groups.length === 0;
  const groupName = (gid: string | null) => groups.find((g) => g.id === gid)?.name ?? 'the unassigned pool';

  // The on-screen order of all media (groups in order, then the pool) — the axis a
  // shift-click range selects along. Independent of collapse (a range can span a
  // collapsed group; the count still reflects it).
  const orderedIds = [...groups.flatMap((g) => inGroup(g.id).map((m) => m.id)), ...pool.map((m) => m.id)];

  // #GR9(a) selection. Plain click toggles one; shift-click adds the contiguous range
  // from the last click. Called from the per-card checkbox.
  const selectItem = (id: string, shift: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (shift && lastClickRef.current) {
        const a = orderedIds.indexOf(lastClickRef.current);
        const b = orderedIds.indexOf(id);
        if (a >= 0 && b >= 0) {
          const [lo, hi] = a < b ? [a, b] : [b, a];
          for (let k = lo; k <= hi; k++) next.add(orderedIds[k]);
        }
      } else {
        next.has(id) ? next.delete(id) : next.add(id);
      }
      return next;
    });
    lastClickRef.current = id;
  };
  // Select-all / clear for a whole group or the pool (clears the "73 clicks" pain).
  const toggleSelectAll = (ids: string[]) => {
    const allSelected = ids.length > 0 && ids.every((id) => selected.has(id));
    setSelected((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
      return next;
    });
  };

  // #GR9(a/b) bulk fan-out over the SELECTION, on the existing per-item endpoints, with
  // honest reconciliation (never a silent partial — same lesson as the uploader #GR9g).
  async function doBulk(ids: string[], action: (id: string) => Promise<unknown>, verb: string) {
    if (ids.length === 0 || live) return;
    setBusy(true);
    try {
      const r = await runBulk(ids, action);
      await load();
      onChange?.(); // #12 cross-view refresh
      clearSel();
      setToast(reconcileMessage(verb, r));
    } finally {
      setBusy(false);
    }
  }
  const bulkMove = (groupId: string | null) => {
    const ids = [...selected];
    if (ids.length === 0) return;
    void doBulk(ids, (id) => api('media-assign', { mediaId: id, groupId }), 'moved');
  };
  const bulkDelete = () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (!confirm(`Delete ${ids.length} selected item${ids.length === 1 ? '' : 's'}? This removes the files from Cloudflare too and can't be undone.`)) return;
    void doBulk(ids, (id) => api('media-delete', { mediaId: id }), 'deleted');
  };

  // #GR9(b) drag-to-assign: drag an item (or, if it's in the selection, the whole set)
  // onto a gallery/pool card to reassign. Additive to multi-select, never a replacement.
  const dropOnGroup = (targetGid: string | null) => {
    const dragged = dragIdRef.current;
    dragIdRef.current = null;
    setDropId(null);
    if (!dragged || live) return;
    const ids = (selected.has(dragged) ? [...selected] : [dragged]).filter(
      (id) => media.find((m) => m.id === id)?.group_id !== targetGid,
    );
    if (ids.length === 0) return;
    void doBulk(ids, (id) => api('media-assign', { mediaId: id, groupId: targetGid }), 'moved');
  };

  // Whole-client completeness total (§5): one honest count across ALL the client's media,
  // never per-gallery — so a gap is never hidden inside a collapsed group.
  const needAttention = media.filter((m) => scoreMedia(m).missing.length > 0).length;

  // Slice 4: the filter predicate — search over title · tags · filename, plus the
  // gaps-only "Needs attention" toggle (scoreMedia). Honest counts above stay on the FULL
  // set; this only governs what's shown. Derived; no data touched.
  const q = search.trim().toLowerCase();
  const filterActive = q !== '' || needsOnly;
  const matches = useCallback(
    (m: Med): boolean => {
      if (needsOnly && scoreMedia(m).missing.length === 0) return false;
      if (!q) return true;
      const tags = Array.isArray(m.metadata?.tags) ? (m.metadata!.tags as unknown[]).join(' ').toLowerCase() : '';
      const fn = (m.original_filename ?? '').toLowerCase();
      return titleOf(m).toLowerCase().includes(q) || tags.includes(q) || fn.includes(q);
    },
    [q, needsOnly],
  );
  // The flat, filtered set for the spreadsheet — in the same on-screen order as the gallery.
  const spreadsheetItems = orderedIds
    .map((id) => media.find((m) => m.id === id))
    .filter((m): m is Med => !!m && matches(m));
  // The pool's filtered cards (true position kept, like the groups).
  const poolShown = pool.map((m, i) => ({ m, pos: i + 1 })).filter((x) => matches(x.m));
  const poolOpen = isOpen(POOL_KEY) || (filterActive && poolShown.length > 0);

  return (
    <div className="space-y-4">
      <Toast toast={toast} onClose={() => setToast(null)} />

      {/* One-screen header + compact action toolbar (visual-cms.md §4 + Slice 1c): the media
          grid is the hero; the setup controls support it. `Media library (N)` with the
          WHOLE-CLIENT attention count clustered immediately to its right (§5), then a tight
          toolbar row — the view pill + Export together (the mockup's `toolbar`). */}
      <div className="space-y-2">
        <div className="flex items-baseline gap-2.5">
          <h3 className={sectionTitle}>Media library ({media.length})</h3>
          {needAttention > 0 ? (
            <span className="text-sm font-medium text-warning">{needAttention} of {media.length} need attention</span>
          ) : media.length > 0 ? (
            <span className="text-sm font-medium text-success">all set</span>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-lg border border-line bg-paper-2 p-0.5" role="group" aria-label="Media view">
            <button
              type="button"
              aria-pressed={view === 'gallery'}
              onClick={() => setView('gallery')}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                view === 'gallery' ? 'bg-paper text-ink shadow-sm' : 'text-ink-2 hover:text-ink'
              }`}
            >
              ▦ Gallery
            </button>
            {/* Slice 3: the Spreadsheet half is now live — a row-based inline-edit grid. */}
            <button
              type="button"
              aria-pressed={view === 'spreadsheet'}
              onClick={() => setView('spreadsheet')}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                view === 'spreadsheet' ? 'bg-paper text-ink shadow-sm' : 'text-ink-2 hover:text-ink'
              }`}
            >
              ▤ Spreadsheet
            </button>
          </div>

          {/* Slice 4: search + Needs-attention filter — governs both views. */}
          {media.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <label className="relative">
                <span className="sr-only">Search media</span>
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search titles, tags, filenames…"
                  className="w-56 rounded border border-line bg-paper px-2.5 py-1.5 text-sm"
                />
              </label>
              <button
                type="button"
                aria-pressed={needsOnly}
                onClick={() => setNeedsOnly((v) => !v)}
                className={`rounded-full border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                  needsOnly ? 'border-warning-bright bg-warning-bright/15 text-warning' : 'border-line bg-paper text-ink-2 hover:text-ink'
                }`}
                title="Show only items with missing fields"
              >
                ● Needs attention{needAttention > 0 ? ` (${needAttention})` : ''}
              </button>
              {filterActive && (
                <button
                  type="button"
                  onClick={() => {
                    setSearch('');
                    setNeedsOnly(false);
                  }}
                  className="text-xs text-ink-2 underline hover:text-ink"
                >
                  Clear
                </button>
              )}
            </div>
          )}
          {/* Slice 2c: Export moved OUT of the toolbar to its own strip below (it's the last
              step — after galleries + metadata are set). The toolbar keeps view + search/filter. */}
        </div>
      </div>

      {live && <p className="rounded border border-warning-bright bg-warning-bright/10 p-2 text-sm text-warning">🔒 Site is live/protected — structuring is read-only.</p>}
      {isLiveClient && !live && (
        <p className="rounded border border-warning-bright bg-warning-bright/10 p-2 text-sm text-warning">
          Live — maintenance mode: media edits save on the preview branch; the live site changes only on a deliberate publish. (Export stays off — it’s part of publishing.)
        </p>
      )}

      {/* Slice 3: the SPREADSHEET view — a flat inline-edit grid over the filtered set. */}
      {view === 'spreadsheet' && <MediaSpreadsheet items={spreadsheetItems} live={live} onSave={saveItem} />}

      {/* ─── Gallery view: the structuring surface (create · add media · galleries · pool) ─── */}
      {view === 'gallery' && (
        <>
      {/* #GR7: galleries-first — the create affordance stays at the TOP (set up galleries
          before uploading; shows with zero media). Compact single row; the fuller guidance
          tucks into a collapsible so the grid stays the hero (no "Step 1 ·" wizard framing). */}
      {!live && (
        <div className="nm-tile space-y-2 rounded-lg p-3">
          <p className="text-xs font-semibold text-ink">Add a gallery or project</p>
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && createGroup()}
              placeholder="New gallery / project name"
              className="min-w-[12rem] flex-1 rounded border border-line bg-paper px-2 py-1.5 text-sm"
            />
            <select value={newKind} onChange={(e) => setNewKind(e.target.value as 'gallery' | 'project')} className="rounded border border-line px-2 py-1 text-sm">
              <option value="project">Project</option>
              <option value="gallery">Gallery</option>
            </select>
            <button onClick={createGroup} disabled={busy || !newName.trim()} className={btnSolid}>
              <FlapText>Add</FlapText>
            </button>
          </div>
          <details className="text-[11px] text-ink-2">
            <summary className="cursor-pointer select-none hover:text-ink">How galleries &amp; projects work</summary>
            <p className="mt-1">
              Create the <b>galleries</b> and <b>projects</b> the site is built from, then file photos into them
              (upload straight into a gallery below, or drop into the pool and assign here). Deleting a group never
              deletes its photos — they return to the unassigned pool. In <b>Build</b> you can point the agent at any
              asset by its <b>filename</b> or its <b>position number</b> (the badge on each thumbnail).
            </p>
          </details>
        </div>
      )}

      {/* #GR7 nudge: loose media but no galleries yet. */}
      {showNudge && (
        <p className="rounded border border-warning-bright bg-warning-bright/10 p-2 text-xs text-warning">
          You have {pool.length} loose image{pool.length === 1 ? '' : 's'} in the pool. Create a gallery above to
          organize them — or leave them in the unassigned pool, that&rsquo;s fine too.
        </p>
      )}

      {/* Add media — the two "get content/metadata in" paths grouped with equal weight:
          Upload (drop-zone) + Apply-from-CSV (input path #1, no longer a buried paragraph
          below Export). Both handlers unchanged; the uploader's own catalogue stays hidden. */}
      {!live && (
        <div className="space-y-2.5 rounded border border-line bg-paper p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-2">Add media</p>
          {uploader && <div>{uploader}</div>}
          {/* VS-1b: apply a filled CSV/JSON PACK to EXISTING media (matched by filename) — an
              obvious button at parity with Upload, not a paragraph. Slice 1e: the whole block is
              also a drop target (drop a CSV/JSON → the SAME applyManifest, no second code path). */}
          <div
            onDragOver={(e) => {
              if (busy) return;
              e.preventDefault();
              if (!csvDragOver) setCsvDragOver(true);
            }}
            onDragLeave={() => setCsvDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setCsvDragOver(false);
              if (busy) return;
              const f = e.dataTransfer.files?.[0];
              if (f) void applyManifest(f);
            }}
            className={`flex flex-wrap items-center gap-2 rounded border border-dashed p-2.5 transition-colors ${
              csvDragOver ? 'border-ink bg-paper' : 'border-line bg-paper-2'
            }`}
          >
            <label className={`${btnSolid} cursor-pointer ${busy ? 'pointer-events-none opacity-40' : ''}`}>
              <FlapText>Apply metadata from a CSV</FlapText>
              <input
                type="file"
                accept=".csv,.tsv,.json,.txt,text/csv,application/json"
                disabled={busy}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void applyManifest(f);
                  e.currentTarget.value = ''; // allow re-picking the same file after a fix
                }}
                className="hidden"
              />
            </label>
            <span className="min-w-[14rem] flex-1 text-[11px] text-ink-2">
              Already uploaded? Drop the client’s filled <b>CSV</b> (or JSON) to fill titles, descriptions + the
              video-SEO PACK on the matching files — matched by <b>filename</b>; values merge, blanks are left as
              they are, any unmatched filename is reported (never silently skipped).
            </span>
            {applyMsg && <p className="w-full text-[11px] text-ink-2">{applyMsg}</p>}
          </div>
        </div>
      )}

      {/* Slice 2c: Export strip — the LAST step, between Add-media and the galleries. Gated on
          the RAW is_live (isLiveClient), NOT the edit-lock: media-export still 403s a live client
          (the deferred renderClientMedia fold-in), so it stays hidden on live even for an admin
          maintaining — don't surface a control that would 403 (go-live-protection §4 note). */}
      {!isLiveClient && (
        <div className="flex flex-wrap items-center gap-3 rounded border border-line bg-paper p-3">
          <p className="min-w-[14rem] flex-1 text-sm text-ink">
            Once you’ve set up your galleries, filled in the metadata and checked everything,
            Export media to branch so the system picks up all the new data.
          </p>
          <button
            onClick={() => void exportMedia()}
            disabled={busy || exporting}
            className={`${btnSolid} ml-auto`}
            title="Writes src/data/media.ts — every film/image by filename → its Stream UID / Images ID + URLs, so a hand-build session can resolve assets directly."
          >
            <FlapText>{exporting ? 'Exporting…' : 'Export media to branch'}</FlapText>
          </button>
        </div>
      )}

      {/* Galleries — the structuring group cards. */}
      {groups.length > 0 && (
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-2">Galleries</p>
      )}

      {/* #GR9(i) jump-to-gallery nav: expand + scroll a card into view without hunting. */}
      {groups.length > 1 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] text-ink-2">Jump to:</span>
          {groups.map((g) => (
            <button
              key={g.id}
              onClick={() => jumpTo(g.id)}
              className="rounded-full border border-line bg-paper px-2 py-0.5 text-[11px] text-ink-2 hover:bg-paper-2"
              title={`${g.name} — ${inGroup(g.id).length} item${inGroup(g.id).length === 1 ? '' : 's'}`}
            >
              {g.name} <span className="text-ink-2">({inGroup(g.id).length})</span>
            </button>
          ))}
          {pool.length > 0 && (
            <button
              onClick={() => jumpTo(POOL_KEY)}
              className="rounded-full border border-line border-dashed bg-paper px-2 py-0.5 text-[11px] text-ink-2 hover:bg-paper-2"
            >
              Unassigned <span className="text-ink-2">({pool.length})</span>
            </button>
          )}
        </div>
      )}

      {/* #GR9(a) selection action bar — sticky so it stays reachable while scrolling a
          long catalogue. Reassign (move) or delete the whole selection at once, on the
          existing per-item endpoints with honest reconciliation. */}
      {/* DS-sweep (2-4a) note: this is a cohesive ON-DARK chrome cluster (bg-gray-900 bar
          + gray-600 dividers/borders + gray-800 select + white/red action buttons). The
          13-token neutrals are hex-var (no /opacity) and there is no on-dark token set, so
          it's left raw as one unit — same call as BuildChat's steering-wheel bar (2-3),
          to be tokenised together in the future on-dark-chrome / <LiveBanner> structural PR. */}
      {!live && selected.size > 0 && (
        <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 rounded border border-gray-900 bg-gray-900 p-2 text-sm text-white shadow">
          <span className="font-medium">{selected.size} selected</span>
          <button onClick={clearSel} className="rounded border border-gray-600 px-2 py-0.5 text-xs text-gray-200 hover:bg-gray-800">
            Clear
          </button>
          <span className="mx-1 h-4 w-px bg-gray-600" />
          <label className="flex items-center gap-1 text-xs text-gray-200">
            Move to
            <select
              value={bulkTarget}
              onChange={(e) => setBulkTarget(e.target.value)}
              className="rounded border border-gray-600 bg-gray-800 px-1.5 py-0.5 text-xs text-white"
            >
              <option value="">Unassigned pool</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </label>
          <button
            onClick={() => bulkMove(bulkTarget || null)}
            disabled={busy}
            className="rounded bg-white px-2.5 py-1 text-xs font-medium text-gray-900 hover:bg-gray-100 disabled:opacity-40"
          >
            Move
          </button>
          <button
            onClick={bulkDelete}
            disabled={busy}
            className="ml-auto rounded bg-red-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-40"
          >
            Delete {selected.size}
          </button>
        </div>
      )}

      {/* Nothing filed yet and no loose media: galleries-first empty state. */}
      {!live && media.length === 0 && groups.length === 0 && (
        <p className="rounded border border-line border-dashed bg-paper p-3 text-sm text-ink-2">
          No media yet. Create a gallery or project above, then upload photos into it — or upload to the pool and
          organize here.
        </p>
      )}

      {/* Groups — Slice 2e: each gallery is a RAISED, clearly-separated section card
          (the cockpit's .card neumorphic lift, brand §3 — NOT the flat modal, §5a is
          modals-only), with more gap between galleries via this space-y-5 wrapper. */}
      <div className="space-y-5">
      {groups.map((g, gi) => {
        const items = inGroup(g.id);
        const pack = packForKind(g.kind);
        // Per-gallery "N need attention" (§5) — this gallery's OWN items only (the
        // whole-client total stays in the header). Derived; no data touched.
        const gNeed = items.filter((m) => scoreMedia(m).missing.length > 0).length;
        // Slice 4: filter for DISPLAY while keeping each item's TRUE position (the build
        // agent's "image N" badge must stay stable under a search). An active filter force-
        // reveals matching cards even in a collapsed group; clearing restores the collapse.
        const shown = items.map((m, i) => ({ m, pos: i + 1 })).filter((x) => matches(x.m));
        const open = isOpen(g.id) || (filterActive && shown.length > 0);
        return (
          <div
            key={g.id}
            ref={(el) => {
              if (el) cardRefs.current.set(g.id, el);
              else cardRefs.current.delete(g.id);
            }}
            onDragOver={(e) => {
              if (!live && dragIdRef.current) {
                e.preventDefault();
                if (dropId !== g.id) setDropId(g.id);
              }
            }}
            onDragLeave={() => setDropId((prev) => (prev === g.id ? null : prev))}
            onDrop={(e) => {
              e.preventDefault();
              dropOnGroup(g.id);
            }}
            className={`card space-y-2 border border-line p-4 scroll-mt-4 ${dropId === g.id ? 'ring-2 ring-ink' : ''}`}
          >
            <div className="flex items-center gap-2">
              {/* #GR8 type toggle — Gallery ⇄ Project. Static badge when live (read-only). */}
              {/* DS-sweep (2-4a): the gallery/project kind chips were categorical colour
                  (emerald/indigo) with no home in the 13 tokens → neutralised to
                  bg-paper-2/text-ink-2; the chip/option text still names the type. Flagged
                  for ① in case a categorical accent is wanted later. */}
              {live ? (
                <span className="rounded bg-paper-2 px-1.5 py-0.5 text-[10px] uppercase text-ink-2">{g.kind}</span>
              ) : (
                <select
                  value={g.kind}
                  disabled={busy}
                  onChange={(e) => changeKind(g, e.target.value as 'gallery' | 'project')}
                  title="Change type (gallery ⇄ project)"
                  className="flex-none rounded border border-line bg-paper-2 px-1 py-0.5 text-[10px] uppercase text-ink-2 disabled:opacity-40"
                >
                  <option value="gallery">gallery</option>
                  <option value="project">project</option>
                </select>
              )}
              <input
                defaultValue={g.name}
                disabled={live}
                onBlur={(e) => renameGroup(g.id, e.target.value)}
                className="min-w-0 flex-1 rounded border border-line px-2 py-1 text-sm font-medium disabled:border-transparent disabled:bg-transparent"
              />
              <span className="text-[10px] text-ink-2">{items.length} item{items.length === 1 ? '' : 's'}</span>
              {gNeed > 0 && (
                <span className="rounded-full bg-warning-bright px-2 py-0.5 text-[10px] font-semibold text-ink">{gNeed} need attention</span>
              )}
              {!live && (
                <div className="flex flex-none items-center gap-1">
                  {items.length > 0 && (
                    <button
                      onClick={() => toggleSelectAll(items.map((m) => m.id))}
                      disabled={busy}
                      className="rounded border border-line px-1.5 py-0.5 text-[11px] text-ink-2 hover:bg-paper-2 disabled:opacity-30"
                      title="Select every item in this gallery"
                    >
                      {items.every((m) => selected.has(m.id)) ? 'Deselect' : 'Select all'}
                    </button>
                  )}
                  {items.length > 1 && (
                    <button
                      onClick={() => setArrangeId(arrangeId === g.id ? null : g.id)}
                      disabled={busy}
                      className={`rounded border border-line px-1.5 py-0.5 text-[11px] disabled:opacity-30 ${arrangeId === g.id ? 'bg-ink text-paper' : 'text-ink-2 hover:bg-paper-2'}`}
                      title="Drag thumbnails to reorder"
                    >
                      {arrangeId === g.id ? 'Done' : 'Arrange ⇅'}
                    </button>
                  )}
                  <button onClick={() => moveGroup(gi, -1)} disabled={gi === 0 || busy} className="rounded border border-line px-1.5 text-xs disabled:opacity-30">↑</button>
                  <button onClick={() => moveGroup(gi, 1)} disabled={gi === groups.length - 1 || busy} className="rounded border border-line px-1.5 text-xs disabled:opacity-30">↓</button>
                  <button
                    onClick={() => setConfirmDelId(confirmDelId === g.id ? null : g.id)}
                    disabled={busy}
                    title="Delete group"
                    className={`rounded border border-line px-1.5 text-xs disabled:opacity-30 ${confirmDelId === g.id ? 'bg-danger-bright text-white' : 'text-danger hover:bg-danger-bright/10'}`}
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>

            {/* #GR9(e) two-option delete confirm — explicit about which it does, with the
                photo count named (#GR9d clarity). Keep-photos is the safe default; the
                destructive path also permanently deletes the N photos (CF teardown). */}
            {!live && confirmDelId === g.id && (
              <div className="space-y-2 rounded border border-danger bg-danger-bright/10 p-2 text-xs">
                <p className="font-medium text-danger">
                  Delete “{g.name}”? It has {items.length} photo{items.length === 1 ? '' : 's'}.
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => deleteGroupKeepPhotos(g)}
                    disabled={busy}
                    className="rounded border border-line bg-paper px-2.5 py-1 font-medium text-ink hover:bg-paper-2 disabled:opacity-40"
                  >
                    Delete group, keep photos (→ pool)
                  </button>
                  <button
                    onClick={() => void deleteGroupWithPhotos(g)}
                    disabled={busy}
                    className="rounded bg-danger-bright px-2.5 py-1 font-medium text-white hover:bg-danger disabled:opacity-40"
                  >
                    Delete group AND its {items.length} photo{items.length === 1 ? '' : 's'} (permanent)
                  </button>
                  <button
                    onClick={() => setConfirmDelId(null)}
                    disabled={busy}
                    className="rounded px-2 py-1 text-ink-2 hover:bg-paper disabled:opacity-40"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* PACK metadata */}
            <details className="rounded border border-line bg-paper-2">
              <summary className="cursor-pointer px-2 py-1 text-xs text-ink-2">Details (location, year, credits…)</summary>
              <div className="grid grid-cols-1 gap-2 p-2 sm:grid-cols-2">
                {pack.map((f) => (
                  <label key={f.key} className={`text-xs text-ink-2 ${f.type === 'textarea' ? 'sm:col-span-2' : ''}`}>
                    {f.label}
                    {f.type === 'textarea' ? (
                      <textarea
                        defaultValue={(g.metadata?.[f.key] as string) ?? ''}
                        disabled={live}
                        placeholder={f.placeholder}
                        onBlur={(e) => savePack(g, f.key, e.target.value.trim())}
                        className="mt-0.5 w-full rounded border border-line px-2 py-1 text-xs"
                        rows={2}
                      />
                    ) : (
                      <input
                        defaultValue={(g.metadata?.[f.key] as string) ?? ''}
                        disabled={live}
                        placeholder={f.placeholder}
                        onBlur={(e) => savePack(g, f.key, e.target.value.trim())}
                        className="mt-0.5 w-full rounded border border-line px-2 py-1 text-xs"
                      />
                    )}
                  </label>
                ))}
              </div>
            </details>

            {/* group items — Arrange grid (#9b drag-to-reorder) or the detail list. */}
            {items.length === 0 ? (
              <p className="text-xs text-ink-2">No media yet — assign from the pool below.</p>
            ) : arrangeId === g.id ? (
              <div>
                <p className="mb-2 text-xs text-ink-2">Drag the thumbnails to reorder. Switch to <b>Done</b> to edit alt / captions.</p>
                <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
                  {items.map((m, i) => (
                    <li
                      key={m.id}
                      draggable={!busy}
                      onDragStart={() => setDragIdx(i)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (dragIdx !== null) reorderInGroup(items, dragIdx, i);
                        setDragIdx(null);
                      }}
                      onDragEnd={() => setDragIdx(null)}
                      title={m.alt || m.kind}
                      className={`relative cursor-move overflow-hidden rounded border border-line bg-paper-2 ${dragIdx === i ? 'opacity-40' : ''}`}
                      style={{ aspectRatio: m.width && m.height ? `${m.width} / ${m.height}` : '4 / 3' }}
                    >
                      {(m.poster ?? m.url) && (
                        <img
                          src={m.poster ?? m.url ?? ''}
                          alt=""
                          onLoad={(e) => healDims(m, e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)}
                          className="h-full w-full object-cover"
                        />
                      )}
                      {/* DS-sweep (2-4a): on-image overlay chip — translucent dark badge over
                          the thumbnail. Left raw (13-token neutrals are hex-var, no /opacity;
                          a solid token would be unreadable over arbitrary imagery). */}
                      <span className="absolute left-0 top-0 rounded-br bg-gray-900/80 px-1 text-[10px] font-medium text-white">{i + 1}</span>
                      {/* Filename strip — so a partial upload set is reconcilable against the source
                          folder at a glance (was: name only in the item details → delete-all-and-redo). */}
                      {m.original_filename && (
                        <span
                          className="absolute inset-x-0 bottom-0 truncate bg-gray-900/80 px-1 py-0.5 text-[10px] font-mono text-white"
                          title={m.original_filename}
                        >
                          {m.original_filename}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div>
                {/* #GR9(i): controlled collapse (persisted, default closed) — native
                    <details open> re-expanded on every remount. Hidden under an active
                    filter (Slice 4), which governs visibility itself. */}
                {!filterActive ? (
                  <button
                    onClick={() => toggleOpen(g.id)}
                    className="cursor-pointer text-xs font-medium text-ink-2 hover:text-ink"
                  >
                    {isOpen(g.id) ? '▾ Hide items' : `▸ Show ${items.length} item${items.length === 1 ? '' : 's'}`}
                  </button>
                ) : (
                  <p className="text-xs font-medium text-ink-2">
                    {shown.length} of {items.length} match{shown.length === 1 ? 'es' : ''}
                  </p>
                )}
                {open && (
                  <ul className="mt-2 grid list-none grid-cols-2 gap-3 lg:grid-cols-3 2xl:grid-cols-4">
                    {shown.map(({ m, pos }) => (
                      <MediaTile
                        key={m.id}
                        m={m}
                        position={pos}
                        live={live}
                        busy={busy}
                        groups={groups}
                        isCover={g.cover_media_id === m.id}
                        selected={selected.has(m.id)}
                        onSelect={(shift) => selectItem(m.id, shift)}
                        onDragStart={() => (dragIdRef.current = m.id)}
                        editing={editingId === m.id}
                        onToggleEdit={() => setEditingId((cur) => (cur === m.id ? null : m.id))}
                        onOpen={() => setDetailId(m.id)}
                        onCover={() => setCover(g.id, m.id)}
                        onAssign={(gid) => assign(m.id, gid)}
                        onField={(field, v) => editField(m.id, field, v)}
                        onMeta={(f, v) => editMeta(m.id, f, v)}
                        onDelete={() => removeMedia(m.id, m.alt || m.kind, groupName(g.id))}
                        onDims={(w, hh) => healDims(m, w, hh)}
                        onMove={pos - 1 === 0 && pos - 1 === items.length - 1 ? undefined : (dir) => moveInGroup(items, pos - 1, dir)}
                        canUp={pos - 1 > 0}
                        canDown={pos - 1 < items.length - 1}
                      />
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        );
      })}
      </div>

      {/* Catalogue — the loose / unassigned pool at the bottom. Controlled collapse
          (persisted, default closed). Also a drop target: dragging items here unassigns
          them (#GR9b). */}
      <p className="text-xs font-semibold uppercase tracking-wide text-ink-2">Catalogue</p>
      <div
        ref={(el) => {
          if (el) cardRefs.current.set(POOL_KEY, el);
          else cardRefs.current.delete(POOL_KEY);
        }}
        onDragOver={(e) => {
          if (!live && dragIdRef.current) {
            e.preventDefault();
            if (dropId !== POOL_KEY) setDropId(POOL_KEY);
          }
        }}
        onDragLeave={() => setDropId((prev) => (prev === POOL_KEY ? null : prev))}
        onDrop={(e) => {
          e.preventDefault();
          dropOnGroup(null);
        }}
        className={`space-y-2 rounded border border-line border-dashed bg-paper p-3 scroll-mt-4 ${dropId === POOL_KEY ? 'ring-2 ring-ink' : ''}`}
      >
        {pool.length === 0 ? (
          <h4 className="text-xs font-semibold text-ink-2">Unassigned (0) — everything’s in a group.</h4>
        ) : (
          <div>
            <div className="flex items-center gap-2">
              {!filterActive ? (
                <button
                  onClick={() => toggleOpen(POOL_KEY)}
                  className="cursor-pointer text-xs font-semibold text-ink-2 hover:text-ink"
                >
                  {isOpen(POOL_KEY) ? '▾' : '▸'} Unassigned ({pool.length})
                </button>
              ) : (
                <span className="text-xs font-semibold text-ink-2">
                  Unassigned — {poolShown.length} of {pool.length} match{poolShown.length === 1 ? 'es' : ''}
                </span>
              )}
              {!live && (
                <button
                  onClick={() => toggleSelectAll(pool.map((m) => m.id))}
                  disabled={busy}
                  className="rounded border border-line px-1.5 py-0.5 text-[11px] text-ink-2 hover:bg-paper-2 disabled:opacity-30"
                  title="Select every unassigned item"
                >
                  {pool.every((m) => selected.has(m.id)) ? 'Deselect' : 'Select all'}
                </button>
              )}
            </div>
            {poolOpen && (
              <ul className="mt-2 list-none gap-3 [column-fill:balance] columns-2 sm:columns-3 [&>li]:mb-3">
                {poolShown.map(({ m, pos }) => (
                  <MediaTile
                    key={m.id}
                    m={m}
                    position={pos}
                    live={live}
                    busy={busy}
                    groups={groups}
                    isCover={false}
                    selected={selected.has(m.id)}
                    onSelect={(shift) => selectItem(m.id, shift)}
                    onDragStart={() => (dragIdRef.current = m.id)}
                    editing={editingId === m.id}
                    onToggleEdit={() => setEditingId((cur) => (cur === m.id ? null : m.id))}
                    onOpen={() => setDetailId(m.id)}
                    onAssign={(gid) => assign(m.id, gid)}
                    onField={(field, v) => editField(m.id, field, v)}
                    onMeta={(f, v) => editMeta(m.id, f, v)}
                    onDelete={() => removeMedia(m.id, m.alt || m.kind, 'the unassigned pool')}
                    onDims={(w, hh) => healDims(m, w, hh)}
                  />
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

        </>
      )}

      {/* Slice 2: the detail MODAL — centred over a dimmed backdrop. Prev/next walk
          `orderedIds` (groups-in-order then the pool), reflecting the live layout. Rendered
          only while the open id still resolves to a media row (a delete behind it closes it). */}
      {(() => {
        if (!detailId) return null;
        const idx = orderedIds.indexOf(detailId);
        const item = media.find((x) => x.id === detailId);
        if (idx < 0 || !item) return null;
        return (
          <MediaDetailModal
            item={item}
            index={idx}
            total={orderedIds.length}
            live={live}
            onPrev={() => idx > 0 && setDetailId(orderedIds[idx - 1])}
            onNext={() => idx < orderedIds.length - 1 && setDetailId(orderedIds[idx + 1])}
            onClose={() => setDetailId(null)}
            onSave={saveItem}
          />
        );
      })()}
    </div>
  );
}

/**
 * #GR9(c) grid tile. A dense, aspect-TRUE (#9a) thumbnail for the multi-column masonry
 * grid — glanceable info (position · kind · orientation · cover · selection) stays on
 * the tile; the per-item edit affordances that don't tile (alt/caption/assign/cover/
 * move/delete) collapse behind a ✎ toggle so 200+ thumbnails scan fast without hiding
 * the actions. Delete is always one tap on the tile; the rest are one ✎ away.
 */
function MediaTile({
  m,
  position,
  live,
  busy,
  groups,
  isCover,
  selected,
  onSelect,
  onDragStart,
  editing,
  onToggleEdit,
  onOpen,
  onCover,
  onAssign,
  onField,
  onMeta,
  onDelete,
  onDims,
  onMove,
  canUp,
  canDown,
}: {
  m: Med;
  position: number; // 1-based position within its group/pool — the build agent's "image N"
  live: boolean;
  busy: boolean;
  groups: Grp[];
  isCover: boolean;
  selected?: boolean;
  onSelect?: (shift: boolean) => void;
  onDragStart?: () => void;
  editing?: boolean;
  onToggleEdit?: () => void;
  onOpen?: () => void; // Slice 2: open the detail modal (thumbnail + body are the click target)
  onCover?: () => void;
  onAssign: (groupId: string | null) => void;
  onField: (field: 'caption' | 'alt', value: string) => void;
  onMeta?: (field: VideoPackField, value: string) => void;
  onDelete: () => void;
  onDims: (width: number, height: number) => void;
  onMove?: (dir: -1 | 1) => void;
  canUp?: boolean;
  canDown?: boolean;
}) {
  const orient = orientationOf(m);
  const health = scoreMedia(m);
  return (
    <li
      // #GR9(b): draggable onto a gallery/pool card to (bulk-)reassign. Selection-aware
      // drop is handled by the card; here we just publish which item is being dragged.
      draggable={!live && !!onDragStart}
      onDragStart={onDragStart}
      className={`group relative flex flex-col overflow-hidden rounded border bg-paper ${selected ? 'ring-2 ring-ink' : 'border-line'}`}
    >
      {/* Visual-CMS card preview (visual-cms.md §4): a uniform 16:10 so the cards tile ~3
          across in a clean grid; orientation stays legible via the badge below. Slice 2: a
          click here opens the detail modal (the overlays below stopPropagation so a select/
          cover/edit/delete tap never also opens it). */}
      <div
        onClick={onOpen}
        role={onOpen ? 'button' : undefined}
        title={onOpen ? 'Open details' : undefined}
        className={`relative grid aspect-[16/10] w-full place-items-center overflow-hidden bg-paper-2 ${onOpen ? 'cursor-pointer' : ''}`}
      >
        {(m.poster ?? m.url) ? (
          <img
            src={m.poster ?? m.url ?? ''}
            alt=""
            loading="lazy"
            onLoad={(e) => onDims(e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)}
            className="h-full w-full object-cover"
          />
        ) : (
          <span className="text-[11px] text-ink-2">no preview</span>
        )}
        {/* centred play glyph for video (Neil-confirmed on Slice 1) */}
        {m.kind === 'video' && (m.poster ?? m.url) && (
          <span className="pointer-events-none absolute grid h-11 w-11 place-items-center rounded-full bg-black/40 text-white" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
          </span>
        )}

        {/* top-left: selection checkbox (glanceable, always on the tile). Same shift-
            click-on-mousedown handling as before (draggable parents suppress the click). */}
        {!live && onSelect && (
          <input
            type="checkbox"
            checked={!!selected}
            onChange={() => {}}
            onMouseDown={(e) => {
              e.stopPropagation(); // the checkbox is a select target, not a drag handle
              onSelect(e.shiftKey);
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation(); // never bubble to the preview's open-modal click
            }}
            title="Select (shift-click for a range)"
            className="absolute left-1.5 top-1.5 h-4 w-4 cursor-pointer accent-ink"
          />
        )}

        {/* top-right: cover star (glanceable) + the edit/delete actions. */}
        {/* DS-sweep (2-4a): the on-image overlay chips below (cover star, ✎/🗑 action
            buttons, position/kind/orientation badges) are all TRANSLUCENT layers sitting
            over arbitrary imagery. The 13-token neutrals are hex-var (no /opacity) and a
            solid token would be illegible over a photo, and the video badge is categorical
            (purple) with no token home — so this whole overlay set is left raw + flagged,
            same pattern as the arrange-grid badge above. */}
        <div className="absolute right-1.5 top-1.5 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {isCover && <span className="rounded bg-amber-400/95 px-1 text-[11px] font-medium text-white" title="Gallery cover">★</span>}
          {!live && onToggleEdit && (
            <button
              onMouseDown={(e) => e.stopPropagation()}
              onClick={onToggleEdit}
              title="Edit alt / caption / assign"
              className={`rounded px-1 text-[11px] leading-4 text-white ${editing ? 'bg-gray-900' : 'bg-gray-900/70 hover:bg-gray-900'}`}
            >
              ✎
            </button>
          )}
          {!live && (
            <button
              onMouseDown={(e) => e.stopPropagation()}
              onClick={onDelete}
              disabled={busy}
              title="Delete this item"
              className="rounded bg-gray-900/70 px-1 text-[11px] leading-4 text-white hover:bg-red-600 disabled:opacity-40"
            >
              🗑
            </button>
          )}
        </div>

        {/* bottom: position number (the build agent's "image N") · kind · orientation. */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center gap-1 bg-gradient-to-t from-black/60 to-transparent px-1.5 pb-1 pt-5 text-[10px] text-white">
          <span className="rounded bg-black/50 px-1 font-medium">{position}</span>
          {m.kind === 'video' ? (
            <span className="rounded bg-purple-500/80 px-1 uppercase">video</span>
          ) : (
            <span className="rounded bg-info/80 px-1 uppercase">image</span>
          )}
          {orient && <span className="rounded bg-white/25 px-1">{orient}</span>}
        </div>
      </div>

      {/* Health-card body (visual-cms.md §4/§5): title + category + the completeness meter
          and missing-field chips, so a gap is obvious without opening the editor. Slice 2: a
          click here opens the detail modal too (same target as the preview). */}
      <div
        onClick={onOpen}
        className={`flex flex-1 flex-col gap-2 p-2.5 ${onOpen ? 'cursor-pointer' : ''}`}
      >
        <div className="truncate text-sm font-semibold text-ink" title={titleOf(m)}>{titleOf(m)}</div>
        <div className="-mt-1 truncate text-xs text-ink-2">{categoryOf(m)}</div>
        <div className="flex flex-wrap gap-1.5">
          {health.missing.length > 0 ? (
            health.missing.slice(0, 3).map((f) => (
              <span key={f.key} className="rounded-full bg-warning-bright px-2 py-0.5 text-[10px] font-semibold text-ink">+ {f.label}</span>
            ))
          ) : (
            <span className="rounded-full bg-success-bright/10 px-2 py-0.5 text-[10px] font-semibold text-success">✓ complete</span>
          )}
        </div>
        <div className="mt-auto flex items-center gap-2">
          <span className="flex flex-1 gap-1">
            {healthFieldsFor(m.kind).map((f) => (
              <span key={f.key} className={`h-[5px] flex-1 rounded-full ${hasField(m, f.key) ? 'bg-success' : 'bg-warning-bright'}`} />
            ))}
          </span>
          <span className="whitespace-nowrap text-[11px] font-semibold tabular-nums text-ink-2">{health.filled}/{health.total}</span>
        </div>
      </div>

      {/* Expandable editor (✎) — the per-item affordances that don't tile. Rendered only
          when open, so defaultValue re-seeds from the latest saved value each time. */}
      {!live && editing && (
        <div className="space-y-1.5 border-t border-line p-2">
          {/* #GR11: the filename the build agent references ("use <group>/<name>"). */}
          {m.original_filename && (
            <p className="truncate font-mono text-[10px] text-ink-2" title={m.original_filename}>{m.original_filename}</p>
          )}
          <input
            defaultValue={m.alt}
            placeholder="alt text (accessibility — required)"
            onBlur={(e) => onField('alt', e.target.value.trim())}
            className="w-full rounded border border-line px-2 py-1 text-xs"
          />
          <input
            defaultValue={m.caption ?? ''}
            placeholder="caption (optional, shown on the site)"
            onBlur={(e) => onField('caption', e.target.value.trim())}
            className="w-full rounded border border-line px-2 py-1 text-xs"
          />

          {/* VS-1: per-VIDEO editorial PACK → media.metadata (media-model §3a). Only for
              video; all optional. `description` is the priority ask. Manifest can pre-fill
              these; this is the manual per-field override. Stream-auto fields aren't asked. */}
          {m.kind === 'video' && onMeta && (
            <details className="rounded border border-line bg-paper-2" open>
              <summary className="cursor-pointer px-2 py-1 text-[11px] font-medium text-ink-2">
                Video SEO — title, description…
              </summary>
              <div className="space-y-1.5 p-2 pt-1">
                {VIDEO_PACK.map((f) => {
                  const seed =
                    f.type === 'tags'
                      ? serializeTags(m.metadata?.[f.key])
                      : f.type === 'moments'
                        ? serializeKeyMoments(m.metadata?.[f.key])
                        : (m.metadata?.[f.key] as string | undefined) ?? '';
                  const label = `${f.label}${f.priority ? ' ★' : ''}`;
                  return (
                    <label key={f.key} className="block">
                      <span className="mb-0.5 block text-[10px] text-ink-2" title={f.help}>{label}</span>
                      {f.type === 'textarea' || f.type === 'moments' ? (
                        <textarea
                          defaultValue={seed}
                          placeholder={f.placeholder}
                          rows={f.type === 'moments' ? 3 : 2}
                          onBlur={(e) => onMeta(f, e.target.value)}
                          className="w-full rounded border border-line px-2 py-1 text-xs"
                        />
                      ) : (
                        <input
                          defaultValue={seed}
                          placeholder={f.placeholder}
                          onBlur={(e) => onMeta(f, e.target.value)}
                          className="w-full rounded border border-line px-2 py-1 text-xs"
                        />
                      )}
                    </label>
                  );
                })}
              </div>
            </details>
          )}

          <div className="flex flex-wrap items-center gap-1">
            <select
              value={m.group_id ?? ''}
              disabled={busy}
              onChange={(e) => onAssign(e.target.value || null)}
              className="max-w-[9rem] rounded border border-line px-1 py-0.5 text-[11px]"
            >
              <option value="">Unassigned</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
            {onMove && (
              <>
                <button onClick={() => onMove(-1)} disabled={!canUp || busy} className="rounded border border-line px-1.5 text-xs disabled:opacity-30" title="Move earlier">↑</button>
                <button onClick={() => onMove(1)} disabled={!canDown || busy} className="rounded border border-line px-1.5 text-xs disabled:opacity-30" title="Move later">↓</button>
              </>
            )}
            {onCover && !isCover && (
              <button onClick={onCover} disabled={busy} className="rounded border border-line px-1.5 py-0.5 text-[10px] text-ink-2 hover:bg-paper-2 disabled:opacity-30">
                set cover
              </button>
            )}
            <button onClick={onToggleEdit} className="ml-auto rounded border border-line px-1.5 py-0.5 text-[10px] text-ink-2 hover:bg-paper-2">
              Done
            </button>
          </div>
        </div>
      )}
    </li>
  );
}
